import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {
  Button,
  ConfirmActionSwal,
  DropdownButton,
  Table,
  TableBody,
  TableTabs,
  TableWrapper,
} from '../../../../../../_gori/partials/widgets'
import {TableSkeleton} from '../../../../../../_gori/helpers/components'
import useWindowDimensions from '../../../../../../_gori/hooks/useWindowDimensions'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import clsx from 'clsx'
import {
  COMMON_OUTER_HEIGHT,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  SETTING_TITLE_HEIGHT,
  TABLE_KEY_COLUMN,
} from '../../../../../../_gori/constants'
import SettingsService from '../../../core/_requests'
import {AccountsModal} from './AccountsModal'
import {ManagerModal} from './ManagerModal'
import {ManagersFilter} from './ManagersFilter'
import {MANAGER_STATUSES} from '../../../core/_const'
import {ManagerFormData, ManagerListItem} from '../../../core/_types'
import {getManagerName} from './_helpers'
import {useDisabled} from '../../../../../../_gori/helpers/components/PageDisabledProvider'
import {useAuth} from '../../../../auth'
import {setLoadingActionTable} from '../../../../../../_gori/helpers/CommonHelpers'
import {EmptyTable} from '../../../../common/components/EmptyTable'

const ManagersPage: React.FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()
  const [loadingFirst, setLoadingFirst] = useState<boolean>(true)
  const {width} = useWindowDimensions()
  const [showModal, setShowModal] = useState<{
    manager: boolean
    accounts: boolean
  }>({
    manager: false,
    accounts: false,
  })
  const [editingManager, setEditingManager] = useState<ManagerListItem | null>(null)
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false)
  const [deactivatingManager, setDeactivatingManager] = useState<ManagerListItem | null>(null)
  const [showActivateConfirm, setShowActivateConfirm] = useState(false)
  const [activatingManager, setActivatingManager] = useState<ManagerListItem | null>(null)
  const [selectedManager, setSelectedManager] = useState<ManagerListItem | null>(null)

  const [tableData, setTableData] = useState<ManagerListItem[]>([])
  const [pagination, setPagination] = useState<any>({})
  const [statistics, setStatistics] = useState<any>([])
  const {setPageDisabled} = useDisabled()
  const {loadingSwitch} = useAuth()

  const fetchManagers = useCallback(async () => {
    setPageDisabled(true)
    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
        search_text: searchParams.get('search_text') || null,
        status: searchParams.get('status') || null,
      },
      cancelToken: newCancelToken(),
    }

    try {
      const res = await SettingsService.getManagers(config)
      if (res) {
        setPagination(res.pagination || {})
        setTableData(res.managers || [])
        setStatistics(res.statistics || [])
      }
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setPageDisabled(false)
      setLoadingFirst(false)
    }
  }, [setPageDisabled, searchParams, newCancelToken, isCancel])

  const reload = useCallback(() => {
    fetchManagers()
  }, [fetchManagers])

  useEffect(() => {
    if (loadingSwitch) return
    fetchManagers()

    return () => {}
  }, [searchParams, loadingSwitch, fetchManagers])

  const handleOpenDeactivateConfirm = useCallback((manager: ManagerListItem) => {
    setDeactivatingManager(manager)
    setShowDeactivateConfirm(true)
  }, [])

  const handleOpenActivateConfirm = useCallback((manager: ManagerListItem) => {
    setActivatingManager(manager)
    setShowActivateConfirm(true)
  }, [])

  const deactivateManager = useCallback(
    async (manager: ManagerFormData) => {
      if (!manager?.branch?.id) return false

      try {
        setPageDisabled(true)
        const {message} = await SettingsService.updateManagerStatus(
          manager.branch.id,
          {status: false},
          {cancelToken: newCancelToken()}
        )
        toast.success(intl.formatMessage({id: message ?? 'UPDATED_SUCCESSFULLY'}))
        await fetchManagers()
        return true
      } catch (error) {
        if (isCancel(error)) return false
        return false
      }
    },
    [setPageDisabled, newCancelToken, intl, fetchManagers, isCancel]
  )

  const activeManager = useCallback(
    async (manager: ManagerFormData) => {
      if (!manager?.branch?.id) return false

      try {
        setPageDisabled(true)
        const {message} = await SettingsService.updateManagerStatus(
          manager.branch.id,
          {status: true},
          {cancelToken: newCancelToken()}
        )
        toast.success(intl.formatMessage({id: message ?? 'UPDATED_SUCCESSFULLY'}))
        await fetchManagers()
        return true
      } catch (error) {
        if (isCancel(error)) return false
        return false
      }
    },
    [setPageDisabled, newCancelToken, intl, fetchManagers, isCancel]
  )

  const resendInvitation = useCallback(
    async (manager: ManagerFormData) => {
      if (!manager?.branch?.id) return false

      try {
        setTableData(setLoadingActionTable(tableData, [manager.id], true))
        const {message} = await SettingsService.resendInvitation(
          manager.branch.id,
          {token: ''},
          {cancelToken: newCancelToken()}
        )
        toast.success(intl.formatMessage({id: message ?? 'UPDATED_SUCCESSFULLY'}))
        return true
      } catch (error) {
        if (isCancel(error)) return false
        return false
      } finally {
        setTableData(setLoadingActionTable(tableData, [manager.id], false))
      }
    },
    [intl, newCancelToken, isCancel, tableData]
  )

  const handleDeactivateManager = useCallback(async () => {
    if (!deactivatingManager) return
    const success = await deactivateManager(deactivatingManager)
    if (success) {
      setShowDeactivateConfirm(false)
      setDeactivatingManager(null)
    }
  }, [deactivatingManager, deactivateManager])

  const handleActivateManager = useCallback(async () => {
    if (!activatingManager) return
    const success = await activeManager(activatingManager)
    if (success) {
      setShowActivateConfirm(false)
      setActivatingManager(null)
    }
  }, [activatingManager, activeManager])

  const handleOpenAccountsModal = useCallback((manager: ManagerListItem) => {
    if (!manager?.branch?.id) return
    setSelectedManager(manager)
    setShowModal((prev) => ({...prev, accounts: true}))
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'manager_name',
        Header: intl.formatMessage({id: 'MANAGER_NAME'}),
        headerClassName: 'min-w-50px',
        cellClassName: 'text-dark',
        Cell: ({row}: {row: any}) => <div>{getManagerName(row.original)}</div>,
      },
      {
        id: 'email',
        Header: intl.formatMessage({id: 'EMAIL'}),
        headerClassName: 'min-w-50px',
        cellClassName: 'text-dark',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.email}</div>
        },
      },
      {
        id: 'branch',
        Header: intl.formatMessage({id: 'BRANCH'}),
        headerClassName: 'min-w-50px text-center',
        cellClassName: 'text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.branch?.name}</div>
        },
      },
      {
        id: 'status',
        Header: intl.formatMessage({id: 'STATUS'}),
        accessor: 'status',
        headerClassName: 'w-250px text-center',
        cellClassName: 'w-250px text-center',
        Cell: ({row}: {row: any}) => {
          return (
            <span
              className={`manager-status-badge-${row.original?.status?.value
                ?.replaceAll('_', '-')
                ?.toLowerCase()} px-4 py-2 rounded-pill w-100 d-flex justify-content-center`}
            >
              {row.original.status?.label}
            </span>
          )
        },
      },
      {
        id: 'accounts_count',
        Header: intl.formatMessage({id: 'ACCOUNTS'}),
        headerClassName: 'min-w-50px text-center',
        cellClassName: 'text-dark text-center',
        Cell: ({row}: {row: any}) => {
          const count = row.original.accounts_count
          return (
            <div className='cursor-pointer' onClick={() => handleOpenAccountsModal(row.original)}>
              {count}
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        headerClassName: 'text-center',
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          const listDropDownBtn = [
            {
              label: intl.formatMessage({id: 'ACTIVATE'}),
              action: () => handleOpenActivateConfirm(row.original),
              className: 'cursor-pointer',
              isVisible: row.original.status?.value === MANAGER_STATUSES.DISABLED.value,
            },
            {
              label: intl.formatMessage({id: 'DEACTIVATE'}),
              action: () => handleOpenDeactivateConfirm(row.original),
              className: 'cursor-pointer',
              isVisible: row.original.status?.value === MANAGER_STATUSES.ACTIVE.value,
            },
            {
              label: intl.formatMessage({id: 'RESEND_INVITE'}),
              action: () => resendInvitation(row.original),
              className: 'cursor-pointer',
              isVisible: row.original.status?.value === MANAGER_STATUSES.PENDING.value,
            },
          ]

          return (
            <div className='d-flex justify-content-between mw-150px'>
              <Button
                className='btn btn-sm btn-advanced-secondary'
                label={intl.formatMessage({id: 'EDIT'})}
                disabled={row.original.status?.value !== MANAGER_STATUSES.ACTIVE.value}
                event={() => {
                  setEditingManager(row.original)
                  setShowModal((prev) => ({...prev, manager: true}))
                }}
              />
              <DropdownButton
                loading={row.original.isLoading}
                list={listDropDownBtn.filter((x) => x.isVisible)}
                className='d-flex justify-content-center'
              />
            </div>
          )
        },
      },
    ],
    [
      intl,
      handleOpenActivateConfirm,
      handleOpenDeactivateConfirm,
      handleOpenAccountsModal,
      resendInvitation,
    ]
  )

  useEffect(() => {
    fetchManagers()
  }, [fetchManagers])

  return (
    <>
      <ManagerModal
        show={showModal.manager}
        isEdit={!!editingManager}
        manager={editingManager}
        onDeactivate={deactivateManager}
        reloadTable={reload}
        handleClose={() => {
          setShowModal((prev) => ({...prev, manager: false}))
          setEditingManager(null)
        }}
      />
      <AccountsModal
        show={showModal.accounts}
        manager={selectedManager}
        handleClose={() => {
          setShowModal((prev) => ({...prev, accounts: false}))
          setSelectedManager(null)
        }}
      />
      <ConfirmActionSwal
        show={showDeactivateConfirm}
        title={intl.formatMessage({id: 'DEACTIVATE'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleDeactivateManager}
        handleClose={() => {
          setShowDeactivateConfirm(false)
          setDeactivatingManager(null)
        }}
      />
      <ConfirmActionSwal
        show={showActivateConfirm}
        title={intl.formatMessage({id: 'ACTIVE'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleActivateManager}
        handleClose={() => {
          setShowActivateConfirm(false)
          setActivatingManager(null)
        }}
      />
      <div className='d-flex justify-content-start w-100 settings-header table-settings-header mb-8'>
        <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
          {intl.formatMessage({id: 'MANAGERS'})}
        </h1>
      </div>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <TableWrapper className='rounded-top-left-0-dt'>
              <div className='table-content--inset-inner h-100'>
                <TableBody>
                  <div className='mt-6 d-flex align-items-center justify-content-between'>
                    <div className='col-3'>
                      <span className='fs-1 col-12 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
                    </div>
                    <div className='col-2 d-flex flex-row-reverse'>
                      <span className='btn col-5 placeholder placeholder-lg rounded-2 bg-secondary' />
                    </div>
                  </div>
                  <hr className='mb-8' />
                  <TableSkeleton countRow={8} />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <>
            <TableTabs
              keyCheckActive='status'
              dataTabs={statistics}
              classNameTabs='managers-tabs'
              classNameChild='mt-0'
              children={
                <TableWrapper className='rounded-top-left-0-dt'>
                  <div className='table-content--inset-inner h-100'>
                    <ManagersFilter />
                    <TableBody
                      height={`calc(100vh - ${COMMON_OUTER_HEIGHT - SETTING_TITLE_HEIGHT}px)`}
                      className='pb-global-banner'
                    >
                      {!pagination?.total ? (
                        <EmptyTable label='NO_DATA_AVAILABLE_TO_VIEW' />
                      ) : (
                        <Table
                          columns={columns}
                          data={tableData}
                          pagination={pagination}
                          tbodyClass='text-gray-600 fw-bold'
                          usePagination
                        />
                      )}
                    </TableBody>
                  </div>
                </TableWrapper>
              }
              childrenTab={
                <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
                  <>
                    <Button
                      className={clsx('btn btn-advanced-primary btn-sm fs-5 mb-3', {
                        'w-100': width <= 375,
                      })}
                      label={intl.formatMessage({id: 'ADD_MANAGER'})}
                      event={() => {
                        setEditingManager(null)
                        setShowModal((prev) => ({...prev, manager: true}))
                      }}
                    />
                  </>
                </CSSTransition>
              }
            />
          </>
        )}
      </CSSTransition>
    </>
  )
}

export {ManagersPage}
